import { IChannel } from "@Interfaces";
import { Http } from "@Services";
import {
    CreateChannelRequest,
    UpdateChannelRequest,
    CreateChannelResponse,
    SuccesfullCreateChannelResponse,
    CreateSubChannelRequest,
    CloneChannelRequest,
    UpdateChannelResponse,
} from "./interfaces";

// Types
interface ApiError {
    response?: {
        status?: number;
        data?: {
            message: string;
            details?: string[];
        };
    };
}

interface ApiErrorResponse {
    status: number;
    message: string;
    details?: string[];
}

// Utilities
const handleApiError = (
    err: any,
    fallbackMessage: string
): ApiErrorResponse => {
    const error = err as ApiError;
    return {
        status: error?.response?.status || 500,
        message: error.response?.data?.message || fallbackMessage,
        details: error.response?.data?.details,
    };
};

const makeRequest = async <T>(
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
    url: string,
    data?: any
): Promise<T> => {
    return Http.Request<T>({
        method,
        url,
        ...(data && { data }),
    });
};

export const postChannel = async (
    data: CreateChannelRequest
): Promise<SuccesfullCreateChannelResponse> => {
    try {
        return await makeRequest<SuccesfullCreateChannelResponse>(
            "POST",
            "/groups",
            data
        );
    } catch (err) {
        // eslint-disable-next-line no-throw-literal
        throw handleApiError(err, "An unexpected error occurred");
    }
};

export const postSubChannel = async (
    parent_channel_id: string,
    data: CreateSubChannelRequest
): Promise<SuccesfullCreateChannelResponse> => {
    try {
        return await makeRequest<SuccesfullCreateChannelResponse>(
            "POST",
            `/groups/${parent_channel_id}/sub-channels`,
            data
        );
    } catch (err) {
        // eslint-disable-next-line no-throw-literal
        throw handleApiError(err, "An unexpected error occurred");
    }
};

export const getUserChannels = async (): Promise<CreateChannelResponse> => {
    try {
        return await makeRequest<CreateChannelResponse>("GET", "/groups");
    } catch (err) {
        // eslint-disable-next-line no-throw-literal
        throw handleApiError(
            err,
            "Failed to fetch channels. Please refresh and try again."
        );
    }
};

export const getChannelPolls = async (channel_id: string): Promise<any> => {
    try {
        return await makeRequest<any>("GET", `/groups/${channel_id}/polls`);
    } catch (err) {
        // eslint-disable-next-line no-throw-literal
        throw handleApiError(
            err,
            "Failed to fetch channel polls. Please try again."
        );
    }
};

export const postCloneChannel = async (
    group_id: string,
    overrides?: CloneChannelRequest
): Promise<SuccesfullCreateChannelResponse> => {
    try {
        return await makeRequest<SuccesfullCreateChannelResponse>(
            "POST",
            `/groups/${group_id}/clone`,
            overrides || {}
        );
    } catch (err) {
        // eslint-disable-next-line no-throw-literal
        throw handleApiError(
            err,
            "Failed to clone channel. Please check your input and try again."
        );
    }
};

export const postDeleteChannel = async (channel_id: string): Promise<void> => {
    try {
        await makeRequest<void>("DELETE", `/groups/${channel_id}`);
    } catch (err) {
        // eslint-disable-next-line no-throw-literal
        throw handleApiError(
            err,
            "Failed to delete channel. Please try again."
        );
    }
};

export const putChannel = async (
    channel: IChannel,
    data: UpdateChannelRequest
): Promise<UpdateChannelResponse> => {
    const {
        title,
        description,
        settings,
        cover_image,
        profile_image,
        active,
        closed,
    } = data;
    const { _id } = channel;
    try {
        return await Http.Request<UpdateChannelResponse>({
            method: "PUT",
            url: `/groups/${_id}`,
            data: {
                description,
                cover_image,
                settings,
                profile_image,
                title,
                active,
                closed,
            },
        });
    } catch (err) {
        // eslint-disable-next-line no-throw-literal
        throw handleApiError(
            err,
            "Failed to update channel. Please check your input and try again."
        );
    }
};

export const getChannelById = async (channel_id: string): Promise<any> => {
    try {
        return await makeRequest<any>("GET", `/groups/${channel_id}`);
    } catch (err) {
        // eslint-disable-next-line no-throw-literal
        throw handleApiError(
            err,
            "Failed to fetch channel details. Please try again."
        );
    }
};

export default {
    postChannel,
    postSubChannel,
    postCloneChannel,
    putChannel,
    getUserChannels,
    getChannelPolls,
    postDeleteChannel,
    getChannelById,
};
